import { Box, Typography } from "@material-ui/core";
import Highcharts from "highcharts";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import LineChart from "src/components/ScoringChart/Line/LineChart";
import { powerfulDateTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import { FigureTypography } from "src/components/Typography/TitleTypography";
import TitleTypography from "src/components/Typography/TitleTypography";
import OverviewMonitoringWrapper from "src/components/Wrapper/OverviewMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserContext } from "src/context/config/userContext";
import {
  useUserMonitoringChartSelector,
  useUserMonitoringSelector,
} from "src/context/monitoring/userMonitoringContext";
import { numberWithCommas, toFixed } from "src/services/utils";

const getOption = () => ({
  chart: {
    height: 260,
  },
  xAxis: {
    type: "datetime",
    labels: {
      enabled: false,
    },
  },
  yAxis: {
    labels: {
      enabled: false,
    },
  },
  ...powerfulDateTooltip(
    (point) => `${point.series.name}(${point.key})`,
    (point) => `$${numberWithCommas(toFixed(point.y, 2))}`
  ),
});

export default function TotalValueLockedOverview() {
  const { pool } = useParams();
  const { role } = useUserContext();
  const { fetchTotalValueLocked } = useUserMonitoringSelector((state) => state.fetch);
  const totalValueLocked = useUserMonitoringChartSelector(MONITORING.USER.totalValueLocked, pool);

  useEffect(() => {
    var currentTime = Math.floor(Date.now() / 1000);
    fetchTotalValueLocked(
      pool,
      { from: currentTime - 2592000, to: currentTime },
      { active: 2, isCheckMetadata: false }
    );
  }, [pool, fetchTotalValueLocked]);

  const { series, tvl, date, check } = useMemo(() => {
    const checkData = totalValueLocked?.tvl?.length > 0;
    if (totalValueLocked && checkData) {
      return {
        check: checkData,
        series: [
          {
            name: "Tvl",
            data: [].concat(totalValueLocked.tvl),
            zoneAxis: "x",
            zones: [].concat(totalValueLocked.zones),
            marker: {
              enabled: true,
              symbol: "circle",
              radius: 6,
            },
          },
        ],
        tvl: totalValueLocked.tvl.slice(-1)[0]["y"],
        date: Highcharts.dateFormat("%b %e, %Y, %k:%M", totalValueLocked.tvl.slice(-1)[0]["x"]),
      };
    }
    return {
      check: checkData,
      series: [{ name: "Tvl", data: [] }],
      supply: 0,
      date: "",
    };
  }, [totalValueLocked]);

  return (
    <OverviewMonitoringWrapper
      title="Total Value Locked"
      overview={
        role === "admin"
          ? "The chart shows the total value locked of users after each of their transactions."
          : "The chart shows your total value locked after each of your transactions."
      }
      to={`/user-monitoring/total-value-locked/${pool}`}
    >
      {check ? (
        <Box>
          <FigureTypography value={`$${numberWithCommas(toFixed(tvl, 2))}`} title="Total Value Lock" />
          <Typography>{date}</Typography>
          <LineChart series={series} option={getOption()} />
        </Box>
      ) : role === "admin" ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <TitleTypography textProps={{ variant: "h4" }} title="This account doesn't have any transactions" />
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <TitleTypography textProps={{ variant: "h4" }} title="You don't have any transactions" />
        </Box>
      )}
    </OverviewMonitoringWrapper>
  );
}
